import { BsMetaUtil, MarketAssetView, MarketInformationAssetData, useTokenListContext } from "@bridgesplit/abf-react";
import { OracleProvider } from "@bridgesplit/abf-sdk";
import { StatProps, Text, TextVariant } from "@bridgesplit/ui";
import { combineCollections, filterTruthy } from "@bridgesplit/utils";

import { OracleTagAddress } from "../common";

// topLevelOracleStats refers to the primary oracles directly listed on the asset.
// These are displayed above the `customOracleStats`, which represent the underlying oracles
interface OracleStats {
    topLevelOracleStats: StatProps[];
    customOracleStats: StatProps[];
}

function extractOracleTypes(data: MarketAssetView | undefined): OracleProvider[] {
    if (!data) return [];
    const assetType = data.asset.oracleProvider;
    const customTypes = (data.customOracles ?? []).map((o) => o.oracleProvider);
    return combineCollections([[assetType], customTypes]).filter(filterTruthy);
}

export function getOracleTypes({
    collateralMarketData,
    principalMarketData
}: {
    collateralMarketData: MarketAssetView | undefined;
    principalMarketData: MarketAssetView | undefined;
}): OracleProvider[] {
    const allOracleTypes = [...extractOracleTypes(principalMarketData), ...extractOracleTypes(collateralMarketData)];
    return Array.from(new Set(allOracleTypes));
}

export function useOracleStats({
    collateralMarketData,
    principalMarketData,
    variant
}: {
    collateralMarketData: MarketAssetView | undefined;
    principalMarketData: MarketAssetView | undefined;
    variant: TextVariant;
}): OracleStats | undefined {
    const { getMetadata } = useTokenListContext();

    if (!collateralMarketData || !principalMarketData) return undefined;

    const collateralMarketAsset = collateralMarketData.asset;
    const principalMarketAsset = principalMarketData.asset;

    if (!collateralMarketAsset || !principalMarketAsset) return undefined;

    // Main metadata
    const collateralMetadata = getMetadata(collateralMarketAsset.assetIdentifier);
    const principalMetadata = getMetadata(principalMarketAsset.assetIdentifier);

    // Collect all oracle stats for the direct ("top level") oracles
    const topLevelPrincipalName = BsMetaUtil.getName(principalMetadata);
    const topLevelCollateralName = BsMetaUtil.getName(collateralMetadata);

    const topLevelOracleStats: StatProps[] = [
        {
            caption: (
                <Text color="caption" variant={variant}>
                    {topLevelPrincipalName}
                </Text>
            ),
            value: (
                <OracleTagAddress
                    oracleAccount={principalMarketAsset.oracleAccount}
                    oracleProvider={principalMarketAsset.oracleProvider}
                    variant={variant}
                />
            )
        },
        {
            caption: (
                <Text color="caption" variant={variant}>
                    {topLevelCollateralName}
                </Text>
            ),
            value: (
                <OracleTagAddress
                    oracleAccount={collateralMarketAsset.oracleAccount}
                    oracleProvider={collateralMarketAsset.oracleProvider}
                    variant={variant}
                />
            )
        }
    ];

    const principalCustomOracles = principalMarketData.customOracles;
    const collateralCustomOracles = collateralMarketData.customOracles;

    const combinedCustomOracleStats = combineCollections([principalCustomOracles, collateralCustomOracles])
        .map((m: MarketInformationAssetData) => {
            // By identifier, not oracle account: beam-backed assets share one
            // account, so that would drop unrelated underlyings.
            if (
                m.assetIdentifier === principalMarketAsset.assetIdentifier ||
                m.assetIdentifier === collateralMarketAsset.assetIdentifier
            ) {
                return null;
            }
            const metadata = getMetadata(m.assetIdentifier);
            const name = BsMetaUtil.getName(metadata);

            return {
                key: m.assetIdentifier,
                caption: name,
                value: (
                    <OracleTagAddress
                        oracleAccount={m.oracleAccount}
                        oracleProvider={m.oracleProvider}
                        variant={variant}
                    />
                )
            };
        })
        .filter(filterTruthy);

    return {
        topLevelOracleStats,
        customOracleStats: combinedCustomOracleStats
    };
}
